import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { Panel } from "@/components/Panel/Panel";
import { manifest } from "@/docs/manifest";
import {
  AmortizationPayment,
  getAmoritizationNumberPayments,
} from "@/financeModels/owasco/amortization";
import { formatDecimalPercent } from "@/utils/format";
import { formatMoney } from "@/utils/moneyFormating";
import { Group, Stack, Table } from "@mantine/core";

export type LoanTableProps = {
  loanAmount: number | null;
  interestRate: number | null;
  paymentsPerYear: number | null;
  schedule: number | null;
  loanStartDate: Date;
  payments: AmortizationPayment[];
  docPrefix: "sb7a" | "sellers";
  termLength: number | null;
};

export const LoanTable = ({
  loanAmount,
  interestRate,
  paymentsPerYear,
  loanStartDate,
  payments,
  schedule,
  docPrefix,
  termLength,
}: LoanTableProps) => {
  const yearCount = Math.floor(schedule || 0);
  const monthCount = Math.round(((schedule || 0) % 1) * 12);
  const loanEndDate = payments.at(-1)?.date;
  const plannedSheduledPaymentsCount = getAmoritizationNumberPayments({
    schedule: termLength || schedule || 0,
    paymentPerYear: paymentsPerYear || 0,
  });
  const actualScheduledPaymentsCount = payments.length;
  const totalExtraPayments = payments.reduce(
    (acc, payment) => acc + (payment.fixedExtraPayment || 0),
    0,
  );

  return (
    <Group justify="center">
      <Stack gap="xs">
        <Panel label={"Loan Details"}>
          <Table miw={300}>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>
                  <LabelExplainer
                    label={"Loan Amount"}
                    explanation={
                      <Doc
                        path={
                          `@/docs/pages/model/amortization/${docPrefix}-loan-amount.mdx` as keyof typeof manifest
                        }
                      />
                    }
                  />
                </Table.Td>
                <Table.Td ta={"left"} fw={600}>
                  {formatMoney(loanAmount || 0)}
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <LabelExplainer
                    label={"Interest Rate"}
                    explanation={
                      <Doc
                        path={
                          `@/docs/pages/model/amortization/${docPrefix}-interest-rate.mdx` as keyof typeof manifest
                        }
                      />
                    }
                  />
                </Table.Td>
                <Table.Td ta={"left"} fw={600}>
                  {formatDecimalPercent(interestRate)}
                </Table.Td>
              </Table.Tr>
              {termLength === null && (
                <Table.Tr>
                  <Table.Td>
                    <LabelExplainer
                      label={"Loan Period"}
                      explanation={
                        <Doc
                          path={
                            `@/docs/pages/model/amortization/${docPrefix}-loan-period.mdx` as keyof typeof manifest
                          }
                        />
                      }
                    />
                  </Table.Td>
                  <Table.Td ta={"left"} fw={600}>
                    {Number(yearCount)} years{" "}
                    {monthCount > 0 && <>, {Number(monthCount)} months</>}
                  </Table.Td>
                </Table.Tr>
              )}
              {termLength !== null && (
                <Table.Tr>
                  <Table.Td>
                    <LabelExplainer
                      label={"Term Length"}
                      explanation={
                        <Doc
                          path={`@/docs/pages/model/amortization/sb7a-conventional-term-length.mdx`}
                        />
                      }
                    />
                  </Table.Td>
                  <Table.Td ta={"left"} fw={600}>
                    {Number(termLength) || 0} years
                  </Table.Td>
                </Table.Tr>
              )}
              <Table.Tr>
                <Table.Td>
                  <LabelExplainer
                    label={"Loan Start Date"}
                    explanation={
                      <Doc
                        path={
                          `@/docs/pages/model/amortization/${docPrefix}-loan-start-date.mdx` as keyof typeof manifest
                        }
                      />
                    }
                  />
                </Table.Td>
                <Table.Td ta={"left"} fw={600}>
                  {loanStartDate.toLocaleDateString()}
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <LabelExplainer
                    label={"Loan Payoff Date"}
                    explanation={
                      <Doc
                        path={
                          `@/docs/pages/model/amortization/${docPrefix}-loan-payoff-date.mdx` as keyof typeof manifest
                        }
                      />
                    }
                  />
                </Table.Td>
                <Table.Td ta={"left"} fw={600}>
                  {loanEndDate && loanEndDate.toLocaleDateString()}
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Panel>
      </Stack>
      <Stack gap="xs">
        <Panel label={"Repayment Summary"} miw={480}>
          <Table>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>
                  <LabelExplainer
                    label={"Scheduled Monthly Payment"}
                    explanation={
                      <Doc
                        path={
                          `@/docs/pages/model/amortization/${docPrefix}-scheduled-monthly-payment.mdx` as keyof typeof manifest
                        }
                      />
                    }
                  />
                </Table.Td>
                <Table.Td ta={"left"} fw={600}>
                  {formatMoney(Number(payments.at(-2)?.scheduledPayment || 0))}
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <LabelExplainer
                    label={"Scheduled Periods"}
                    explanation={
                      <Doc
                        path={
                          `@/docs/pages/model/amortization/${docPrefix}-scheduled-periods.mdx` as keyof typeof manifest
                        }
                      />
                    }
                  />
                </Table.Td>
                <Table.Td ta={"left"} fw={600}>
                  {plannedSheduledPaymentsCount} monthly payments
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <LabelExplainer
                    label={"Actual Number of Payments"}
                    explanation={
                      <Doc
                        path={
                          `@/docs/pages/model/amortization/${docPrefix}-actual-number-payments.mdx` as keyof typeof manifest
                        }
                      />
                    }
                  />
                </Table.Td>
                <Table.Td ta={"left"} fw={600}>
                  {actualScheduledPaymentsCount} monthly payments
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <LabelExplainer
                    label={"Total Extra Payments"}
                    explanation={
                      <Doc
                        path={
                          `@/docs/pages/model/amortization/${docPrefix}-total-extra-payments.mdx` as keyof typeof manifest
                        }
                      />
                    }
                  />
                </Table.Td>
                <Table.Td ta={"left"} fw={600}>
                  {formatMoney(totalExtraPayments || 0)}
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <LabelExplainer
                    label={"Total Interest"}
                    explanation={
                      <Doc
                        path={
                          `@/docs/pages/model/amortization/${docPrefix}-total-interest.mdx` as keyof typeof manifest
                        }
                      />
                    }
                  />
                </Table.Td>
                <Table.Td ta={"left"} fw={600}>
                  {formatMoney(payments.at(-1)?.cumulativeInterest || 0)}
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Panel>
      </Stack>
    </Group>
  );
};
